import { Check } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import type { WorkspaceSummary } from '../../../lib/workspaces';
import { Button, Card, Typography } from '../ui';
import { WorkspaceBadge } from '../WorkspaceBadge';
import styles from './BriefSlot.module.css';

// Mobile-card rendering of the "a new workspace was created for you" brief state — the flat desktop
// content (`NewWorkspaceBrief`) presented as a floating `Card`, matching the mobile hero
// (`MobileMaestroBrief`) and the native `BriefNewWorkspaceCard` (Figma 8265:31834): a checkmark
// eyebrow, the workspace badge + name + description, and two equal-width outlined shortcuts.

export function MobileNewWorkspaceBrief({ workspace }: { workspace?: WorkspaceSummary }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  if (!workspace) return null;

  const path = `/workspace/${encodeURIComponent(workspace.slug)}`;
  const click = (button_name: string) => () => logPageClickBi({ page_name: 'home', button_name });

  return (
    <Card component="section" style={{ textAlign: 'start' }} aria-labelledby="brief-new-workspace-title">
      <p className={styles.eyebrow}>
        <Check size={16} aria-hidden />
        <Typography variant="h3" color="strong" component="span">
          {t('brief.newWorkspace.title')}
        </Typography>
      </p>
      <div className={styles.mobileName}>
        <WorkspaceBadge ws={workspace} size={28} />
        <span id="brief-new-workspace-title" className={styles.mobileNameText}>
          {workspace.name}
        </span>
      </div>
      {workspace.description && (
        <Typography variant="p2" color="secondary" component="p" className={styles.mobileSubtitle}>
          {workspace.description}
        </Typography>
      )}
      <div className={styles.mobileActions}>
        <Button
          variant="outlined"
          fullWidth
          href={path}
          onClick={click('brief_new_workspace_growth')}
        >
          {t('brief.newWorkspace.growth')}
        </Button>
        <Button
          variant="outlined"
          fullWidth
          href={`${path}/goals`}
          onClick={click('brief_new_workspace_goals')}
        >
          {t('brief.newWorkspace.goals')}
        </Button>
        <Button
          variant="outlined"
          fullWidth
          href={`${path}/agents`}
          onClick={click('brief_new_workspace_agent_work')}
        >
          {t('brief.newWorkspace.agentWork')}
        </Button>
      </div>
    </Card>
  );
}
